import React from 'react'
import { FBPage } from 'components/FbUI/Page/FBPage'
import { useEntityBreadcrumbs } from 'context/layout'
import type { BreadcrumbItem } from 'components/Layout/Header/types'
import { cn } from 'components/UI/cn'

const EMPTY_BREADCRUMBS: BreadcrumbItem[] = []

interface EntityDetailLayoutProps {
  /** Sidebar content (accordions, stat cards, etc.) */
  sidebar: React.ReactNode
  /** Main content area */
  children: React.ReactNode
  /** Gap between sidebar and content columns (Tailwind spacing scale) */
  gap?: 0 | 3 | 4 | 5 | 6
  /** Gap between items in the right content column */
  contentGap?: 0 | 3 | 4 | 5 | 6
  /** Entity breadcrumbs for the navigation header */
  entityBreadcrumbs?: BreadcrumbItem[]
  /** Additional className for FBPage */
  className?: string
  /** Additional className for the inner content column */
  contentClassName?: string
  /** 'split' = page scrolls, sidebar flows naturally, right column has sticky header (default). 'page' = whole page scrolls together. */
  scrollMode?: 'split' | 'page'
}

const GAP_CLASSES: Record<number, string> = {
  0: 'gap-0',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
}

/**
 * Reusable two-column layout for entity detail pages (deals, companies).
 *
 * Left sidebar and right content scroll independently. Sidebar width is
 * controlled by the sidebar content itself (e.g., w-[300px] or w-[360px]).
 */
export function EntityDetailLayout({
  sidebar,
  children,
  gap = 4,
  contentGap = 3,
  className,
  contentClassName,
  entityBreadcrumbs,
  scrollMode = 'split',
}: EntityDetailLayoutProps) {
  useEntityBreadcrumbs(entityBreadcrumbs ?? EMPTY_BREADCRUMBS)

  const isPageScroll = scrollMode === 'page'

  return (
    <FBPage
      className={cn('gap-y-3', className)}
      noContainer
      innerPageClassName={cn('!pb-0 !pr-0', isPageScroll && 'overflow-y-auto')}
    >
      <div
        className={cn(
          'flex flex-1',
          isPageScroll ? 'min-h-min' : 'min-h-0 overflow-y-auto',
          GAP_CLASSES[gap]
        )}
      >
        <div className="flex-shrink-0 self-start">{sidebar}</div>

        <div className="flex-1 min-w-0">
          <div
            className={cn(
              'flex flex-col mr-5 min-h-full pb-6',
              GAP_CLASSES[contentGap],
              contentClassName
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </FBPage>
  )
}
